"use client"

import { useState, useEffect, useMemo } from "react"
import { parseMappingValue } from "@/lib/parse-mapping-value"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectItemText, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { X, Plus, Check } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/blades/blade-dialog"
import { Combobox } from "@/components/ui/combobox"
import { toast } from "sonner"
import { useValueMappingSets } from "@/hooks/use-value-mapping-sets"
import type { ValueMapping, ValueMappingSet } from "@/types/fbo-api"
import type { AtsInfoState } from "@/types/ui"
import type { FlatMapping } from "@/types/ui"
import { SourceFieldTooltip } from "@/components/shared/source-field-tooltip"
import { ValueMappingDisplay } from "@/components/shared/value-mapping-display"
import { DestinationValueInput } from "@/components/shared/destination-value-input"
import { AtsFieldPathPicker } from "@/components/shared/ats-field-path-picker"
import { ArrayPathRefiner } from "@/components/shared/array-path-refiner"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

interface Entity {
  entityType: string
  name: string
  referenceId?: string
  fieldMappings: any[]
}

interface EditMappingDialogProps {
  mapping: FlatMapping | null
  entities: Entity[]
  primaryEntityType: string
  unmappedDestinationFields: string[]
  atsInfoState: AtsInfoState
  onSave: (updatedMapping: {
    destinationField: string
    sourceField: string
    entityIndex: number
    valueMappings?: ValueMapping[]
    valueMappingSetName?: string | null
    dataTypeConverter?: string | null
    defaultValueMapping?: string | number | null
  }) => void
  onCancel: () => void
}

export function EditMappingDialog({
  mapping,
  entities,
  primaryEntityType,
  unmappedDestinationFields,
  atsInfoState,
  onSave,
  onCancel,
}: EditMappingDialogProps) {
  const { t, n } = useTranslation()
  const isAddMode = !mapping?.isMapped
  
  const [editSourceField, setEditSourceField] = useState("")
  const [editEntityIndex, setEditEntityIndex] = useState<number>(0)
  const [editDestinationField, setEditDestinationField] = useState("")
  const [editValueMappings, setEditValueMappings] = useState<ValueMapping[]>([])
  const [editValueMappingSetName, setEditValueMappingSetName] = useState<string | null>(null)
  const [availableSets, setAvailableSets] = useState<ValueMappingSet[]>([])
  const [editDataTypeConverter, setEditDataTypeConverter] = useState<string | null>(null)
  const [editDefaultValueMapping, setEditDefaultValueMapping] = useState<string>("")
  const [newSourceValue, setNewSourceValue] = useState("")
  const [newDestinationValue, setNewDestinationValue] = useState("")

  const { loadSets } = useValueMappingSets()

  // Load shared value mapping sets when the dialog opens
  useEffect(() => {
    if (!mapping) return
    loadSets()
      .then(setAvailableSets)
      .catch(() => setAvailableSets([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!mapping])

  const selectedSet = useMemo(
    () => availableSets.find((s) => s.name === editValueMappingSetName) ?? null,
    [availableSets, editValueMappingSetName]
  )

  // Find the selected converter's description
  const selectedConverterDescription = useMemo(() => {
    if (!editDataTypeConverter || editDataTypeConverter === "none" || !atsInfoState.data?.supportedDataTypeConverters) {
      return null
    }
    const foundConverter = atsInfoState.data.supportedDataTypeConverters.find(c => c.name === editDataTypeConverter)
    return foundConverter?.description || null
  }, [editDataTypeConverter, atsInfoState.data?.supportedDataTypeConverters])

  // Initialize form when mapping changes
  useEffect(() => {
    if (mapping) {
      setEditSourceField(mapping.sourceField || "")
      setEditEntityIndex(mapping.entityIndex || 0)
      setEditValueMappings(mapping.valueMappings || [])
      setEditValueMappingSetName(mapping.valueMappingSetName || null)
      // Clear invalid dataTypeConverter, keep valid ones
      setEditDataTypeConverter(
        mapping.validationIssues?.invalidDataTypeConverter ? null : (mapping.dataTypeConverter || null)
      )
      setEditDefaultValueMapping(mapping.defaultValueMapping?.toString() || "")
      setEditDestinationField("")
      setNewSourceValue("")
      setNewDestinationValue("")
    }
  }, [mapping])

  const handleAddValueMapping = () => {
    if (!newSourceValue || !newDestinationValue) {
      toast.error(t("editMapping.toasts.errorTitle"), {
        description: t("editMapping.toasts.valuesRequired"),
      })
      return
    }

    const newMapping: ValueMapping = {
      sourceValue: parseMappingValue(newSourceValue, true),
      destinationValue: parseMappingValue(newDestinationValue, true),
    }

    setEditValueMappings([...editValueMappings, newMapping])
    setNewSourceValue("")
    setNewDestinationValue("")
  }

  const handleRemoveValueMapping = (index: number) => {
    const updated = editValueMappings.filter((_, i) => i !== index)
    setEditValueMappings(updated)
  }

  const handleSave = () => {
    if (!mapping) return

    if (!editSourceField) {
      toast.error(t("editMapping.toasts.errorTitle"), {
        description: t("editMapping.toasts.sourceFieldRequired"),
        duration: 3000,
      })
      return
    }

    // Validate destination field for invalid mappings
    if (mapping.validationIssues?.invalidDestinationField && !editDestinationField) {
      toast.error(t("editMapping.toasts.errorTitle"), {
        description: t("editMapping.toasts.destinationFieldRequired"),
      })
      return
    }

    onSave({
      destinationField: mapping.validationIssues?.invalidDestinationField ? editDestinationField : mapping.destinationField,
      sourceField: editSourceField,
      entityIndex: editEntityIndex,
      // When a shared set is referenced its values are materialized server-side;
      // inline values are stripped on save so the set stays the source of truth
      valueMappings: editValueMappingSetName
        ? undefined
        : editValueMappings.length > 0
          ? editValueMappings
          : undefined,
      valueMappingSetName: editValueMappingSetName,
      dataTypeConverter: editDataTypeConverter,
      defaultValueMapping: parseMappingValue(editDefaultValueMapping),
    })
  }

  if (!mapping) return null

  return (
    <Dialog open={!!mapping} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
            <DialogTitle>{t((isAddMode ? "editMapping.title.add" : "editMapping.title.edit"))}</DialogTitle>
          <DialogDescription>
            {mapping.validationIssues?.invalidDestinationField
              ? t("editMapping.description.invalidDestination", { field: mapping.destinationField })
              : t((isAddMode ? "editMapping.description.add" : "editMapping.description.edit"), { field: mapping.destinationField })}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 p-4 max-h-[70vh] overflow-y-auto">
          {/* Show destination field selection only for invalid destination field mappings */}
          {mapping.validationIssues?.invalidDestinationField && (
            <div className="space-y-2">
              <Label htmlFor="editDestinationField" className="font-medium text-destructive">
                {t("editMapping.labels.newDestinationField")}
              </Label>
              <Combobox
                value={editDestinationField}
                onValueChange={setEditDestinationField}
                options={unmappedDestinationFields.map(field => ({ value: field, label: field }))}
                placeholder={t("editMapping.placeholders.destinationField")}
                emptyText={t("editMapping.empty.destinationFields")}
              />
              <p className="text-sm text-muted-foreground">
                {t("editMapping.labels.currentInvalidField")}{" "}
                <span className="font-medium text-destructive">{mapping.destinationField}</span>
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="editEntityIndex">{t("editMapping.labels.entity")}</Label>
            <Select value={String(editEntityIndex)} onValueChange={(value) => setEditEntityIndex(Number(value))}>
              <SelectTrigger>
                <SelectValue placeholder={t("editMapping.placeholders.entity")} />
              </SelectTrigger>
              <SelectContent>
                {entities.map((entity, index) => (
                  <SelectItem key={index} value={String(index)}>
                    {entity.name}
                    {entity.entityType === primaryEntityType && ` ${t("editMapping.labels.primary")}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Label htmlFor="editSourceField">{t("editMapping.labels.sourceField")}</Label>
              <SourceFieldTooltip />
            </div>
            {entities[editEntityIndex] ? (
              <AtsFieldPathPicker
                id="editSourceField"
                entityType={entities[editEntityIndex].entityType}
                value={editSourceField || null}
                onChange={(value) => setEditSourceField(value ?? "")}
                placeholder={t("editMapping.placeholders.sourceField")}
              />
            ) : (
              <Input
                id="editSourceField"
                value={editSourceField}
                onChange={(e) => setEditSourceField(e.target.value)}
              />
            )}
            {entities[editEntityIndex] && editSourceField.includes("[") && (
              <ArrayPathRefiner
                entityType={entities[editEntityIndex].entityType}
                path={editSourceField}
                onChange={setEditSourceField}
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="editDataTypeConverter">{t("editMapping.labels.dataType")}</Label>
            <Select
              value={editDataTypeConverter || "none"}
              onValueChange={(value) => setEditDataTypeConverter(value === "none" ? null : value)}
              disabled={atsInfoState.isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder={atsInfoState.isLoading ? t("common.state.loading") : t("editMapping.placeholders.dataType")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("common.state.none")}</SelectItem>
                {atsInfoState.data?.supportedDataTypeConverters?.map((converter) => (
                  <SelectItem key={converter.name} value={converter.name}>
                    <div className="flex flex-col">
                      <SelectItemText className="font-medium">{converter.name}</SelectItemText>
                      <span className="text-xs text-muted-foreground">{converter.description}</span>
                    </div>
                  </SelectItem>
                ))}
                {!atsInfoState.data?.supportedDataTypeConverters?.length && atsInfoState.data && (
                  <SelectItem value="" disabled>{t("editMapping.empty.converters")}</SelectItem>
                )}
              </SelectContent>
            </Select>
            {/* Show description for selected converter */}
            {selectedConverterDescription && (
              <p className="text-sm text-muted-foreground">
                {selectedConverterDescription}
              </p>
            )}
            {atsInfoState.error && (
              <p className="text-sm text-muted-foreground text-red-600">
                {t("editMapping.errors.converters")}
              </p>
            )}
            {mapping.validationIssues?.invalidDataTypeConverter && (
              <p className="text-sm text-amber-600">
                {t("editMapping.warnings.converterCleared", { converter: mapping.dataTypeConverter })}
              </p>
            )}
          </div>

          <Separator className="my-4" />

          <div className="space-y-4">
            <h4 className="font-medium">{t("editMapping.sections.valueMappings")}</h4>
            <p className="text-sm text-muted-foreground">
              {t("editMapping.sections.valueMappingsHelp")}
            </p>

            {mapping.codeTableName && (
              <div className="p-3 bg-muted rounded-md border">
                <p className="text-sm font-medium">
                  {t("editMapping.codeTable.linked")}{" "}
                  <code className="px-1 py-0.5 bg-background rounded-sm">{mapping.codeTableName}</code>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t("editMapping.codeTable.help")}
                </p>
              </div>
            )}

            {availableSets.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="editValueMappingSet">{t("editMapping.labels.sharedSet")}</Label>
                <Select
                  value={editValueMappingSetName ?? "none"}
                  onValueChange={(value) => setEditValueMappingSetName(value === "none" ? null : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("editMapping.options.noneInline")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("editMapping.options.noneInline")}</SelectItem>
                    {availableSets.map((set) => (
                      <SelectItem key={set.name} value={set.name}>
                        {t((set.valueMappings?.length ?? 0) === 1 ? "editMapping.labels.setValuesOne" : "editMapping.labels.setValuesOther", { name: set.name, count: n(set.valueMappings?.length ?? 0) })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t("editMapping.help.sharedSets")}
                </p>
              </div>
            )}

            {editValueMappingSetName && !selectedSet && (
              <p className="text-sm text-amber-600">
                {t("editMapping.warnings.missingSet", { name: editValueMappingSetName })}
              </p>
            )}

            {selectedSet && (
              <div className="space-y-2">
                <Label>{t("editMapping.labels.valuesFromSet", { name: selectedSet.name })}</Label>
                <div className="border rounded-md p-3 space-y-2 max-h-32 overflow-y-auto">
                  {(selectedSet.valueMappings ?? []).map((valueMapping, index) => (
                    <ValueMappingDisplay
                      key={index}
                      sourceValue={valueMapping.sourceValue}
                      destinationValue={valueMapping.destinationValue}
                      codeTableName={selectedSet.fboCodeTableName ?? mapping?.codeTableName}
                      codeTableValueType={selectedSet.fboCodeTableValueType ?? mapping?.codeTableValueType}
                      className="font-mono bg-muted px-2 py-1 rounded"
                    />
                  ))}
                  {(selectedSet.valueMappings?.length ?? 0) === 0 && (
                    <p className="text-sm text-muted-foreground italic">{t("editMapping.empty.setValues")}</p>
                  )}
                </div>
              </div>
            )}

            {!editValueMappingSetName && editValueMappings.length > 0 && (
              <div className="space-y-2">
                <Label>{t("editMapping.labels.currentValueMappings")}</Label>
                <div className="border rounded-md p-3 space-y-2 max-h-32 overflow-y-auto">
                  {editValueMappings.map((valueMapping, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <ValueMappingDisplay
                        sourceValue={valueMapping.sourceValue}
                        destinationValue={valueMapping.destinationValue}
                        codeTableName={mapping?.codeTableName}
                        codeTableValueType={mapping?.codeTableValueType}
                        className="font-mono bg-muted px-2 py-1 rounded"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveValueMapping(index)}
                        className="h-6 w-6"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!editValueMappingSetName && (
              <div className="grid grid-cols-3 gap-2 items-end">
                <div className="space-y-2">
                  <Label htmlFor="newSourceValue">{t("editMapping.labels.sourceValue")}</Label>
                  <Input
                    id="newSourceValue"
                    placeholder={t("editMapping.placeholders.sourceValue")}
                    value={newSourceValue}
                    onChange={(e) => setNewSourceValue(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newDestinationValue">{t("editMapping.labels.destinationValue")}</Label>
                  <DestinationValueInput
                    value={newDestinationValue}
                    onValueChange={setNewDestinationValue}
                    codeTableName={mapping?.codeTableName}
                    codeTableValueType={mapping?.codeTableValueType}
                    placeholder={t("editMapping.placeholders.destinationValue")}
                    id="newDestinationValue"
                  />
                </div>
                <Button onClick={handleAddValueMapping} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  {t("editMapping.actions.add")}
                </Button>
              </div>
            )}

          </div>

          <Separator className="my-4" />

          {/* Its own section, not part of Value Mappings: the default answers two independent
              questions — nothing arrived, and the value that arrived matched no row — and a field
              can want the first without having a table at all. Nesting it under the table, and
              rendering it only once a row existed, made the "nothing arrived" case unreachable. */}
          <div className="space-y-4">
            <h4 className="font-medium">{t("editMapping.sections.defaultValue")}</h4>
            <div className="space-y-2">
              <Label htmlFor="editDefaultValueMapping">{t("editMapping.labels.defaultValueMapping")}</Label>
              <DestinationValueInput
                value={editDefaultValueMapping}
                onValueChange={setEditDefaultValueMapping}
                codeTableName={mapping?.codeTableName}
                codeTableValueType={mapping?.codeTableValueType}
                placeholder={t("editMapping.placeholders.defaultValueMapping")}
                id="editDefaultValueMapping"
              />
              <p className="text-sm text-muted-foreground">
                {t("editMapping.help.defaultValueMapping")}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t("common.actions.cancel")}
          </Button>
          <Button onClick={handleSave}>
            <Check className="h-4 w-4 mr-2" />
            {t("editMapping.actions.saveChanges")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
} 